<template>
  <div class="flex flex-col p-3">
    <!-- Filter bar -->
    <div class="flex items-center gap-3 py-2 flex-wrap">
      <input type="text" v-model="search" placeholder="Search by BRN"
             class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-48"
             @keyup.enter="fetchData" />
      <select v-model="statuses"
              class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              @change="fetchData">
        <option :value="null">-- Filter by Status --</option>
        <option value="PENDING">Pending</option>
        <option value="SENT">Sent</option>
        <option value="PAUSED">Paused</option>
        <option value="CANCELED">Canceled</option>
        <option value="EXPIRED">Expired</option>
        <option value="FAILED">Failed</option>
        <option value="IN_PROGRESS">In Progress</option>
      </select>
      <select v-model="types"
              class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              @change="fetchData">
        <option :value="null">-- Filter by Type --</option>
        <option
          v-for="option in typeOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.text }}
        </option>
      </select>
      <input type="date" v-model="startDate"
             class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
             @change="fetchData" />
      <input type="date" v-model="endDate"
             class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
             @change="fetchData" />
      <select v-model="limit"
              class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              @change="fetchData">
        <option :value="12">12</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <button @click="clearFilters"
              class="px-3 py-2 text-sm font-medium text-primary bg-white border border-primary rounded-md hover:bg-primary hover:text-white transition-colors flex items-center gap-1"
              title="Clear filters">
        <i class="fa fa-times"></i> Clear
      </button>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>BRN</th>
          <th>Type</th>
          <th>Document Name</th>
          <th>Retry Count</th>
          <th>Status</th>
          <th>Date</th>
          <th>Last Ran</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td v-for="j in 9" :key="j">
              <div class="h-3 bg-gray-200 rounded"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(action, idx) in stream" :key="idx">
            <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
            <td class="font-bold font-mono cursor-pointer hover:underline text-primary"
                @click="searchTerm(action.brn)">{{ action.brn }}
            </td>
            <td class="italic text-blue-600 cursor-pointer hover:underline" @click="searchTerm(action.type)">
              {{ action.type }}
            </td>
            <td class="font-mono text-xs whitespace-nowrap">{{ action.actionIdToken || "—" }}</td>
            <td class="text-center">{{ action.retryCount }}</td>
            <td><span :class="statusPill(action.status)">{{ action.status?.toLowerCase() }}</span></td>
            <td class="text-xs">{{ dateFormat(action.createdAt ?? null) }}</td>
            <td class="text-xs">{{ dateFormat(action.lastRanAt ?? null) }}</td>
            <td>
              <div class="flex gap-1">
                <button v-if="['PAUSED', 'IN_PROGRESS', 'EXPIRED'].includes(action.status)" 
                        class="btn-sm-danger disabled:opacity-50"
                        :disabled="!!processingState[action.id]"
                        @click="redo(action)">
                  <i class="fa mr-1" :class="processingState[action.id] === 'retry' ? 'fa-spinner fa-spin' : 'fa-redo'"></i> Retry
                </button>
                <button class="btn-sm-primary" @click="view(action)" :disabled="!!processingState[action.id]">
                  <i class="fa fa-eye mr-1"></i> View
                </button>
                <button class="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs font-medium rounded disabled:opacity-50 flex items-center transition"
                        :disabled="!!processingState[action.id]"
                        @click="runNow(action)">
                  <i class="fa mr-1" :class="processingState[action.id] === 'run' ? 'fa-spinner fa-spin' : 'fa-play'"></i> 
                  {{ processingState[action.id] === 'run' ? 'Running...' : 'Run' }}
                </button>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="!loading && stream.length === 0">
          <td colspan="9" class="text-center py-6 text-gray-400 text-sm">No results found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1"
         class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2">
      <p class="text-xs text-gray-500">Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
        <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span></p>
      <div class="flex gap-2">
        <button class="btn-sm-primary" @click="previous" :disabled="currentPage === 1">
          <i class="fa fa-chevron-left mr-1"></i> Prev
        </button>
        <button class="btn-sm-primary" @click="next" :disabled="currentPage === pagination.totalPages">
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>

    <!-- Detail Modal -->
    <AppModal v-model="showDetails" size="xl" title="Process Details" hide-footer>
      <div v-if="process" class="space-y-3 text-sm">
        <p><b>BRN:</b> {{ process.brn }}</p>
        <p><b>Type:</b> {{ process.type }}</p>
        <p><b>Action ID Token:</b> {{ process.actionIdToken }}</p>
        <p><b>Description:</b> {{ process.description }}</p>
        <p v-if="process.fileName"><b>File name:</b> {{ process.fileName }}</p>
        <p><b>Retries:</b> {{ process.retryCount }}</p>
        <p><b>Date:</b> {{ dateFormat(process.createdAt ?? null) }}</p>
        <p><b>Endpoint:</b> <code class="text-xs bg-green-50 border border-green-200 px-1 rounded">{{
            process.endPoint
          }}</code></p>
        <div v-if="process.request">
          <p class="font-bold mt-3">Request</p>
          <pre class="text-green-700 text-xs bg-green-50 border border-green-200 p-2 rounded overflow-auto">{{
              parseJsonSafe(process.request)
            }}</pre>
        </div>
        <div v-if="process.response">
          <p class="font-bold mt-3">Response</p>
          <p class="text-red-700 text-xs bg-red-50 border border-red-200 p-2 rounded">{{ process.response }}</p>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { useToast } from "@/composables";
import { api } from "@/app/services/api.service";

const { successToast, errorToast } = useToast();

const showDetails = ref(false);
const loading = ref(false);
const currentPage = ref(1);
const limit = ref(12);
const stream = ref<any[]>([]);
const pagination = ref({ totalElements: 0, totalPages: 0 });
const statuses = ref<string | null>(null);
const types = ref<string | null>(null);
const search = ref("");
const startDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
const process = ref<any | null>(null);
const processingState = ref<Record<string, 'run' | 'retry'>>({});

const typeOptions = [
  { value: "EDMS", text: "EDMS" },
  { value: "BUSINESS_ACCOUNT", text: "Business Account" },
  { value: "CENTRAL_REGISTRY", text: "Central Registry" },
  { value: "DOCUMENT_ID", text: "Document ID" },
  { value: "FETCH_REGISTRY", text: "Fetch Registry" },
  { value: "PUSH_DOCUMENT_ID_TO_REGISTRY", text: "Push Document ID To Registry" },
  { value: "DIGITAL_SIGNING", text: "Digital Signing" },
  { value: "CERTIFICATE", text: "Certificate" },
  { value: "PAYMENTS", text: "Payments" },
  { value: "NON_INDIVIDUAL_REGISTRY", text: "Non-individual Registry" },
  { value: "REGISTER_WITH_NIR", text: "Register With NIR" },
  { value: "NIR_NAME_CHANGE", text: "NIR Name Change" },
  { value: "NIR_SUBTYPE_CHANGE", text: "NIR Subtype Change" },
  { value: "NIR_COMPLIANCE_UPDATE", text: "NIR Compliance Update" },
  { value: "NIR_STATUS_UPDATE", text: "NIR Status Update" },
] as const;

const statusPill = (status: string) => {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  if (status === "SENT") return base + "bg-green-50 text-green-700";
  if (status === "EXPIRED" || status === "FAILED") return base + "bg-red-50 text-red-700";
  if (status === "IN_PROGRESS") return base + "bg-blue-50 text-blue-700";
  if (status === "PAUSED" || status === "CANCELED" || status === "CANCELLED") return base + "bg-orange-50 text-orange-600";
  if (status === "PENDING") return base + "bg-gray-100 text-gray-600";
  return base + "bg-gray-100 text-gray-600";
};

const parseJsonSafe = (jsonStr: string) => {
  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    return jsonStr;
  }
};

const clearFilters = () => {
  search.value = "";
  statuses.value = null;
  types.value = null;
  startDate.value = null;
  endDate.value = null;
  currentPage.value = 1;
  fetchData();
};

const fetchData = () => {
  loading.value = true;
  const resolvedStatuses = statuses.value ?? "";
  
  let path = `/building-societies/approval-actions?page=${currentPage.value}&size=${limit.value}`;
  if (resolvedStatuses) path += `&status=${resolvedStatuses}`;
  if (types.value) path += `&type=${types.value}`;
  if (search.value) path += `&search=${search.value}`;
  if (startDate.value) path += `&startDate=${startDate.value}`;
  if (endDate.value) path += `&endDate=${endDate.value}`;

  api().get(path).then(r => {
    stream.value = r.data?.content || r.data || [];
    pagination.value = { 
      totalElements: r.data?.totalElements || 0, 
      totalPages: r.data?.totalPages || 0 
    };
  }).catch(e => {
    console.error(e);
    errorToast("Error", "Failed to fetch building societies actions");
  }).finally(() => {
    loading.value = false;
  });
};

const searchTerm = (term: string) => {
  search.value = term;
  currentPage.value = 1;
  fetchData();
};

const view = (details: any) => {
  process.value = details;
  showDetails.value = true;
};

const redo = (action: any) => {
  processingState.value[action.id] = 'retry';
  api().put(`/building-societies/approval-actions/${action.id}/status`, { status: "PENDING" })
    .then(() => {
      successToast("Success", "Action retried successfully");
      fetchData();
    })
    .catch(e => {
      errorToast("Error", e.response?.data?.message || "Failed to retry action");
    })
    .finally(() => {
      delete processingState.value[action.id];
    });
};

const runNow = (action: any) => {
  processingState.value[action.id] = 'run';
  api().post(`/building-societies/approval-actions`, { id: action.id })
    .then(() => {
      successToast("Success", "Action ran successfully");
      fetchData();
    })
    .catch(e => {
      errorToast("Error", e.response?.data?.message || "Failed to run action");
    })
    .finally(() => {
      delete processingState.value[action.id];
    });
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetchData();
  }
};
const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchData();
  }
};

onMounted(() => fetchData());
</script>

<style scoped>
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
th {
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}
td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;
}
</style>
